import { AutoComplete, Breadcrumb, DatePickerInput, PopUpModal, SuccessPopupModal, TextInput, Title } from '../../Components/Atoms';
import { CONNECTOR_TYP } from '../../Library/constants';
import { Button, useToast } from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { deleteBatteryFromInventory, saveBatteryDetails, updateBatteryDetail } from '../../APIs';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import PATH from '../../Routes/paths';
import './style.css';
import { getISODate } from '../../Library/datetime';


const FIELD_TYPE = {
  TEXT: 1,
  AUTOCOMPLETE: 2,
  DATEPICKER: 3
};

const initialState = {
  bId: "",
  ampHrs: "",
  vltg: "",
  manf: "",
  cTyp: "",
  exp: "",
  cCycl: "",
};

const AddBattery = () => {
  const [formData, setFormData] = useState(initialState);
  const [formErrors, setFormErrors] = useState({});
  const [successModal, setSuccessModal] = useState({ isOpen: false, msg: "" });
  const [confirmDeleteBattery, setConfirmDeleteBattery] = useState({});
  const [searchParams] = useSearchParams();
  const Navigate = useNavigate();
  const location = useLocation();
  const Toast = useToast();

  const bId = searchParams.get('bid');
  const isEdit = location.state && location.state.batteryDetail;
  const title = isEdit ? "Update Battery" : "Battery Details";

  const setFormFieldData = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {

    if (location.state && location.state.batteryDetail) {
      const newObj = {}

      location.state.batteryDetail['exp'] = getISODate(location.state.batteryDetail['exp']);
      Object.keys(initialState).forEach(field => newObj[field] = location.state.batteryDetail[field] || "")
      
      setFormData(newObj);
    }

  }, [location]);

  const addBatteryFormFields = useMemo(() => [
    {
      type: FIELD_TYPE.TEXT,
      label: "Battery UID",
      name: "bId",
      placeholder: "Enter Battery UID",
      required: true,
      disabled: isEdit ? true : false,
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Ampere Hours",
      name: "ampHrs",
      placeholder: "Enter Ampere Hours",
      required: true
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Voltage",
      name: "vltg",
      placeholder: "Enter Voltage",
      required: true
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Manufacturer",
      name: "manf",
      placeholder: "Enter Manufacturer",
      required: true
    },
    {
      type: FIELD_TYPE.AUTOCOMPLETE,
      label: "Connector Type",
      name: "cTyp",
      placeholder: "Select Connector Type",
      options: Object.entries(CONNECTOR_TYP).map(([key, value]) => ({ label: key, value: value })),
      required: true
    },
    {
      type: FIELD_TYPE.DATEPICKER,
      label: "Warranty Expiry",
      name: "exp",
      required: true
    },
    {
      type: FIELD_TYPE.TEXT,
      label: "Charging Cycles",
      name: "cCycl",
      placeholder: "Enter Charging Cycles",
      required: true,
      regex: /^[0-9]+$/
    },
  ], [isEdit]);

  const saveBatteryData = (batteryData) => {
    let postFunction = saveBatteryDetails;

    if (location.state && location.state.batteryDetail) {
      postFunction = updateBatteryDetail;
      batteryData = { ...batteryData, oldBid: bId };
    }

    postFunction(batteryData, (failure, success) => {
      if (failure){
        const errMsg = JSON.parse(failure).data;
        Toast({ isClosable: true, description: errMsg, position: "bottom", variant: "subtle", duration: 3000 })
      }
      else {
        setFormData(initialState);
        setSuccessModal(() => ({
          isOpen: true,
          msg: isEdit ? "Battery detail updated" : "Battery added successfully"
        }));
      };
    })
  };

  const submitHandler = () => {
    let errors = {}, sendData = {...formData};

    // Checking if any of the required fields are empty, then show error and updating the data to sendData
    for (let field of addBatteryFormFields) {
      let { name, label } = field;
      if (!field.required)
        continue;
      
      if (!formData[name]) {
        errors[name] = `Please enter ${label.toLowerCase()}`;
      } else if (field.regex && !field.regex.test(formData[name])) {
        errors[name] = `Please enter valid ${label.toLowerCase()}`;
      }
    }
    
    // If any of the required input do not have data 
    if (Object.keys(errors).length) return setFormErrors(errors);
    else setFormErrors({});

    if (isEdit) {
      let updatedData = {};
      const batteryDetail = location.state.batteryDetail;

      for (let field of Object.keys(sendData)) {
        if (batteryDetail[field] !== sendData[field] && sendData[field])
          updatedData[field] = sendData[field];
      }

      // During update detail, none of the input updated
      if (!Object.keys(updatedData).length) return Toast({ title: "Nothing to Update", variant: "subtle", position: "bottom", isClosable: true });

      // Parsing the date to ts
      if (updatedData["exp"]) updatedData['exp'] = Date.parse(updatedData['exp']);

      sendData = updatedData;
    }    

    saveBatteryData(sendData);
  };

  const DeleteBattery = (bId) => {
    deleteBatteryFromInventory(bId, (err) => {
      if (err) {
        console.error("AddBattery : Error while delete battery", err);
        setConfirmDeleteBattery({});
        Toast({ isClosable: true, description: "Failed to delete battery", position: "bottom", status: "error", variant: "solid" });
      }
      else {
        setSuccessModal({ isOpen: true, msg: "Battery removed successfully" });
      }
    })
  }

  const deleteBattery = () => {
    setConfirmDeleteBattery({
      open: true,
      modalMsg: "Are you sure you want to permanently remove this battery?",
      confirmBtn: "Remove",
      closeBtn: "Cancel",
      onConfirm: () => DeleteBattery(bId),
      onModalClose: () => setConfirmDeleteBattery({}),
    });
  }

  const breadcrumbPaths = [
    { idx: 1, name: "Inventory Management", link: PATH.INVENTORY },
    { idx: 1, name: "Batteries", link: PATH.BATTERIES },
    { idx: 2, name: title, link: "", isCurrentPage: true },
  ];

  return (
    <section className="panel-main add-battery-main">
      <PopUpModal {...confirmDeleteBattery} size={"md"}/>
      <SuccessPopupModal open={successModal.isOpen} modalMsg={successModal.msg} closeBtn={"Okay"} modalSize={"lg"} onModalClose={() => { setSuccessModal(prev => ({ ...prev, isOpen: false })); Navigate(PATH.BATTERIES) }} />

      <div >
        <Breadcrumb paths={breadcrumbPaths} />
      </div>

      {/* <Button
        mb="8px"
        leftIcon={<AddModelIcon />}
        className="add-battery-bulk-upload-btn"
        onClick={() => Navigate("")}
      >
        Bulk Upload
      </Button> */}
      {
        isEdit && <Button variant={"outline"} className='battery-remove-btn' onClick={deleteBattery}>Remove from Inventory </Button>
      }
      <div className='add-battery'>
        <div className='add-battery-card'>
          <Title>{title}</Title>
          <form className='add-battery-form'>
            {
              addBatteryFormFields.map((formField, idx) => {
                return <React.Fragment key={idx}>
                  {
                    formField.type === FIELD_TYPE.TEXT && (
                      <TextInput
                        inputName={formField.name || ""}
                        placeholder={formField.placeholder}
                        className="add-battery-text-field"
                        label={formField.label}
                        labelClassName="add-battery-text-field-label"
                        errMessage={formErrors[formField.name] || ""}
                        onChange={(value) => setFormFieldData(formField.name, value)}
                        value={formData[formField.name]}
                        isRequired={formField.required}
                        disabled={formField.disabled}
                      />
                    )
                  }
                  {
                    formField.type === FIELD_TYPE.AUTOCOMPLETE && (
                      <AutoComplete
                        options={formField.options}
                        label={formField.label}
                        autoCompleteClassName={"add-battery-autocomplete"}
                        className={"add-battery-autocomplete-field"}
                        labelClassName={"add-battery-autocomplete-field-label"}
                        placeholder={formField.placeholder}
                        errMessage={formErrors[formField.name] || ""}
                        inputName={formField.name}
                        onChange={(value) => setFormFieldData(formField.name, value)}
                        value={formData[formField.name]}
                        isRequired={formField.required}
                      />
                    )
                  }
                  {
                    formField.type === FIELD_TYPE.DATEPICKER && (
                      <DatePickerInput
                        inputName={formField.name || ""}
                        className="add-battery-datepicker-field"
                        label={formField.label}
                        labelClassName="add-battery-datepicker-field-label"
                        DatepickerClassName="add-battery-datepicker"
                        errMessage={formErrors[formField.name] || ""}
                        onChange={(value) => setFormFieldData(formField.name, value)}
                        value={formData[formField.name]}
                        isRequired={formField.required}
                      />
                    )
                  }
                </React.Fragment>
              })
            }
          </form>
        </div>
        <div className="add-battery-submit-button">
          <Button type="submit" className="add-battery-button" onClick={submitHandler}>
            {isEdit ? "Save" : "Add Battery"}
          </Button>
        </div>
      </div>
    </section>
  )
};

export default AddBattery;